import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

interface User {
  id: number;
  email: string;
  brand_name?: string;
  role: string;
  api_key?: string;
  is_superadmin?: boolean;
  plan_code?: string;
  plan_name?: string;
  totp_enabled?: boolean;
  onboarding_completed?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ requires_2fa?: boolean; temp_token?: string }>;
  register: (email: string, password: string, brand_name?: string) => Promise<void>;
  agentLogin: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  setToken: (token: string) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await api.post('/auth/login', { email, password });
          if (data.requires_2fa) {
            set({ isLoading: false });
            return { requires_2fa: true, temp_token: data.temp_token };
          }
          localStorage.setItem('auth-token', data.token);
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
          return {};
        } catch (err: any) {
          const msg = err.response?.data?.message || 'Login failed';
          set({ error: msg, isLoading: false });
          throw err;
        }
      },

      register: async (email, password, brand_name) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await api.post('/auth/register', { email, password, brand_name });
          localStorage.setItem('auth-token', data.token);
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
        } catch (err: any) {
          const msg = err.response?.data?.message || 'Registration failed';
          set({ error: msg, isLoading: false });
          throw err;
        }
      },

      agentLogin: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await api.post('/auth/agent/login', { email, password });
          localStorage.setItem('auth-token', data.token);
          set({ user: data.agent, token: data.token, isAuthenticated: true, isLoading: false });
        } catch (err: any) {
          const msg = err.response?.data?.message || 'Login failed';
          set({ error: msg, isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (e) { /* ignore */ }
        localStorage.removeItem('auth-token');
        set({ user: null, token: null, isAuthenticated: false });
      },

      fetchMe: async () => {
        try {
          const { data } = await api.get('/auth/me');
          set({ user: data.user, isAuthenticated: true });
        } catch (err) {
          set({ user: null, token: null, isAuthenticated: false });
          localStorage.removeItem('auth-token');
        }
      },

      updateProfile: async (profileData) => {
        const { data } = await api.put('/auth/me', profileData);
        set({ user: { ...get().user!, ...data.user } });
      },

      setToken: (token) => {
        localStorage.setItem('auth-token', token);
        set({ token, isAuthenticated: true });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token, user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
